import { create } from "zustand";
import type { NudgeTarget } from "~/api/opsTypes";

/**
 * "Remind me about this" — the picker's state, and nothing else.
 *
 * **One picker open at a time, structurally**: a single optional request, not a list, not a
 * map. kms enforced the same rule with a `pickerOpen`/`pickerTarget`/`pickerAnchor` triple that
 * could in principle disagree with itself; one object cannot.
 *
 * The client half of nudges is already W2's `api.nudges.*`; there is no store method here that
 * talks to the network — a failed reminder must surface through TanStack Query's error path
 * rather than being swallowed to `console.error` and returning null (§E6.11).
 */
export interface NudgePickerRequest {
  target: NudgeTarget;
  /** The trigger's rect. `null` centres the popover — the ⋯ menu has no useful anchor. */
  anchorRect: DOMRect | null;
  defaultNote: string;
  /** Enables the "Before due" instant chip. */
  dueDate: Date | null;
}

export interface NudgePickerState {
  request: NudgePickerRequest | null;
  openPicker: (request: Partial<NudgePickerRequest> & { target: NudgeTarget }) => void;
  closePicker: () => void;
}

export const useNudgePicker = create<NudgePickerState>()((set) => ({
  request: null,
  openPicker: (request) =>
    set({
      request: {
        anchorRect: null,
        defaultNote: "",
        dueDate: null,
        ...request,
      },
    }),
  closePicker: () => set({ request: null }),
}));

/** The first 60 characters are the note kms used, and they are a good note. */
export function noteFrom(text: string): string {
  return text.trim().slice(0, 60);
}
